import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

interface Buyer { id: string; companyName: string; }

export const ContractForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [form, setForm] = useState({
    buyerId: '', contractNumber: '', buyerReference: '', status: 'active',
    coffeeDescription: '', totalQuantityKg: '', totalValue: '', pricePerKg: '',
    currency: 'USD', incoterms: 'FOB Mombasa', shippingMonth: '', notes: '',
  });
  const [buyers, setBuyers] = useState<Buyer[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch('/api/admin/buyers', { credentials: 'include' })
      .then(r => r.json())
      .then(data => setBuyers(Array.isArray(data) ? data : []))
      .catch(() => {});

    if (isEdit) {
      fetch(`/api/admin/export-invoices?resource=contracts`, { credentials: 'include' })
        .then(r => r.json())
        .then(data => {
          const contract = (Array.isArray(data) ? data : []).find((c: any) => c.id === id);
          if (contract) {
            setForm({
              buyerId: contract.buyerId || '',
              contractNumber: contract.contractNumber || '',
              buyerReference: contract.buyerReference || '',
              status: contract.status || 'active',
              coffeeDescription: contract.coffeeDescription || '',
              totalQuantityKg: contract.totalQuantityKg || '',
              totalValue: contract.totalValue || '',
              pricePerKg: contract.pricePerKg || '',
              currency: contract.currency || 'USD',
              incoterms: contract.incoterms || 'FOB Mombasa',
              shippingMonth: contract.shippingMonth || '',
              notes: contract.notes || '',
            });
          }
        });
    }
  }, [id, isEdit]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    if (!form.contractNumber.trim()) {
      setError('Contract number is required');
      setSaving(false);
      return;
    }

    const body = isEdit ? { id, ...form } : form;
    try {
      const res = await fetch('/api/admin/export-invoices?resource=contracts', {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to save');
      navigate('/admin/contracts');
    } catch (err: any) {
      setError(err.message);
      setSaving(false);
    }
  };

  const inputClass = "w-full px-3.5 py-2.5 border border-[#E8E6E2] rounded-xl bg-[#FAFAF8] text-[#1A1A1A] text-sm focus:outline-none focus:ring-2 focus:ring-[#1A1A1A]/10 focus:border-[#1A1A1A]/30 focus:bg-white transition-all placeholder-[#C4C4C4]";
  const labelClass = "block text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold mb-1.5";

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A] tracking-tight">{isEdit ? 'Edit Contract' : 'New Contract'}</h1>
          <p className="text-sm text-[#A3A3A3] mt-1">Purchase contract details</p>
        </div>
        <button onClick={() => navigate('/admin/contracts')} className="text-sm text-[#A3A3A3] hover:text-[#1A1A1A] transition-colors font-medium">
          Back to contracts
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm font-medium flex items-center gap-2">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="flex-shrink-0">
            <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M8 5v3.5M8 10.5v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl p-6 shadow-sm shadow-black/[0.03]">
          <h2 className="text-[11px] font-semibold text-[#1A1A1A] uppercase tracking-[0.1em] mb-5">Contract Details</h2>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>Contract Number *</label>
              <input name="contractNumber" value={form.contractNumber} onChange={handleChange} required placeholder="e.g. 4346P01452" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Buyer</label>
              <select name="buyerId" value={form.buyerId} onChange={handleChange} className={inputClass}>
                <option value="">Select buyer...</option>
                {buyers.map(b => <option key={b.id} value={b.id}>{b.companyName}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Buyer Reference</label>
              <input name="buyerReference" value={form.buyerReference} onChange={handleChange} placeholder="Buyer's internal reference" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Status</label>
              <select name="status" value={form.status} onChange={handleChange} className={inputClass}>
                <option value="active">Active</option>
                <option value="fulfilled">Fulfilled</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Shipping Month</label>
              <input name="shippingMonth" value={form.shippingMonth} onChange={handleChange} placeholder="e.g. Dec 2025" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Incoterms</label>
              <select name="incoterms" value={form.incoterms} onChange={handleChange} className={inputClass}>
                <option value="FOB Mombasa">FOB Mombasa</option>
                <option value="CIF">CIF</option>
                <option value="CFR">CFR</option>
                <option value="EXW">EXW</option>
                <option value="FCA">FCA</option>
              </select>
            </div>
          </div>
        </div>

        <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl p-6 shadow-sm shadow-black/[0.03]">
          <h2 className="text-[11px] font-semibold text-[#1A1A1A] uppercase tracking-[0.1em] mb-5">Coffee & Financials</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className={labelClass}>Coffee Description</label>
              <input name="coffeeDescription" value={form.coffeeDescription} onChange={handleChange} placeholder="e.g. Washed SL28 AA, AB, C grades — Nandi & Kericho" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Total Quantity (kg)</label>
              <input name="totalQuantityKg" type="number" step="0.01" value={form.totalQuantityKg} onChange={handleChange} placeholder="19200" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Price per kg</label>
              <input name="pricePerKg" type="number" step="0.01" value={form.pricePerKg} onChange={handleChange} placeholder="9.50" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Total Value</label>
              <input name="totalValue" type="number" step="0.01" value={form.totalValue} onChange={handleChange} placeholder="190524.00" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Currency</label>
              <select name="currency" value={form.currency} onChange={handleChange} className={inputClass}>
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (&euro;)</option>
                <option value="GBP">GBP (&pound;)</option>
                <option value="KES">KES</option>
              </select>
            </div>
          </div>
        </div>

        <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl p-6 shadow-sm shadow-black/[0.03]">
          <h2 className="text-[11px] font-semibold text-[#1A1A1A] uppercase tracking-[0.1em] mb-5">Notes</h2>
          <textarea name="notes" value={form.notes} onChange={handleChange} rows={3} placeholder="Additional contract terms, quality requirements..." className={inputClass} />
        </div>

        <div className="flex items-center gap-3 pt-2">
          <button type="submit" disabled={saving} className="px-8 py-3 bg-[#1A1A1A] text-white rounded-xl text-sm font-medium hover:bg-[#2a2a2a] active:scale-[0.98] transition-all disabled:opacity-40 shadow-sm shadow-black/10">
            {saving ? 'Saving...' : isEdit ? 'Update Contract' : 'Create Contract'}
          </button>
          <button type="button" onClick={() => navigate('/admin/contracts')} className="px-6 py-3 border border-[#E8E6E2] rounded-xl text-sm text-[#6B6B6B] hover:bg-[#F5F3F0] hover:border-[#E0DDD8] transition-all font-medium">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};
